import { useEffect, useState } from 'react'
import type { RealtimeEvent } from '@sac/contracts'
import { api, devApi, type DevSummary } from '../lib/api'
import { useRealtimeStream } from '../lib/events'

/**
 * /dev/messaging — inspection console for the messaging engine. Not linked from the agent
 * nav; gated server-side by ENABLE_DEV_TOOLS (default off in production).
 */
export function DevMessaging() {
  const [token, setToken] = useState(() => localStorage.getItem('sac_sim_token') ?? 'dev-sim-token')
  const [summary, setSummary] = useState<DevSummary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [liveEvents, setLiveEvents] = useState<RealtimeEvent[]>([])

  function refresh() {
    devApi
      .summary(token)
      .then(setSummary)
      .catch((e) => setError(String(e)))
  }
  useEffect(refresh, [token])
  useEffect(() => {
    localStorage.setItem('sac_sim_token', token)
  }, [token])

  useRealtimeStream(`/api/events`, (e) => {
    setLiveEvents((prev) => [e, ...prev].slice(0, 50))
  })

  async function setSimulate(id: string, mode: 'success' | 'delay' | 'fail') {
    try {
      await api.setChannelAccountSimulate(id, mode)
      refresh()
    } catch (e) {
      setError(String(e))
    }
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 20, fontFamily: 'sans-serif', fontSize: 13 }}>
      <h2>🛠 Console de mensageria (dev)</h2>
      <label style={{ display: 'block', marginBottom: 4 }}>Token</label>
      <input value={token} onChange={(e) => setToken(e.target.value)} style={{ padding: 6, marginBottom: 16, width: 320 }} />
      {error && <div style={{ color: 'crimson', marginBottom: 12 }}>{error}</div>}

      <h3>Contas de canal (simulação)</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
            <th style={{ padding: 6 }}>Conta</th>
            <th>Canal</th>
            <th>Status</th>
            <th>Simular envio</th>
          </tr>
        </thead>
        <tbody>
          {summary?.channelAccounts.map((a) => (
            <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: 6 }}>
                {a.displayName} <span style={{ color: '#999' }}>({a.storeName})</span>
              </td>
              <td>{a.channelType}</td>
              <td>{a.connectionStatus}</td>
              <td>
                {(['success', 'delay', 'fail'] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setSimulate(a.id, m)}
                    style={{
                      marginRight: 6,
                      padding: '3px 8px',
                      borderRadius: 6,
                      border: '1px solid #ccc',
                      background: (a.settings?.simulate ?? 'success') === m ? '#333' : '#fff',
                      color: (a.settings?.simulate ?? 'success') === m ? '#fff' : '#333',
                    }}
                  >
                    {m}
                  </button>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>Mensagens recentes</h3>
      <div style={{ maxHeight: 240, overflowY: 'auto', marginBottom: 24, border: '1px solid #eee', borderRadius: 8 }}>
        {summary?.recentMessages.map((m) => (
          <div key={m.id} style={{ padding: '6px 10px', borderBottom: '1px solid #f5f5f5' }}>
            <b>{m.direction}</b> · {m.senderType} · {m.messageType} · <i>{m.status}</i> · {m.text?.slice(0, 60)}
          </div>
        ))}
      </div>

      <h3>Eventos em tempo real (ao vivo)</h3>
      <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
        {liveEvents.map((e) => (
          <div key={e.id} style={{ padding: '6px 10px', borderBottom: '1px solid #f5f5f5' }}>
            <b>{e.type}</b> · conv {e.conversationId ?? '—'} · {new Date(e.at).toLocaleTimeString('pt-BR')}
          </div>
        ))}
        {liveEvents.length === 0 && <div style={{ padding: 10, color: '#999' }}>Aguardando eventos…</div>}
      </div>
    </div>
  )
}
